/**
 * Soren Margin Answer Card.
 * Renders exact margin figures with the mandatory completeness line.
 * Nothing estimated.
 */

import { formatMoney } from '../../lib/formatters'

interface MarginData {
  projectName?: string
  projectNumber?: string
  revisedContract?: number | string
  commitments?: number | string
  directCosts?: number | string
  projectedMargin?: number | string
  marginPct?: string
  completenessText?: string
}

export default function SorenMarginAnswerCard({
  data,
  onAddCost,
  onOpenMargin,
}: {
  data?: MarginData
  onAddCost?: () => void
  onOpenMargin?: () => void
}) {
  const revised = data?.revisedContract != null ? formatMoney(data.revisedContract) : '$0.00'
  const commits = data?.commitments != null ? formatMoney(data.commitments) : '$0.00'
  const directs = data?.directCosts != null ? formatMoney(data.directCosts) : '$0.00'
  const projected = data?.projectedMargin != null ? formatMoney(data.projectedMargin) : '$0.00'
  const marginPct = data?.marginPct || '0.0%'
  const completeness =
    data?.completenessText ||
    'Nothing estimated. Based on entered contract, commitments, and direct costs. Labor and overhead will be reflected once configured.'

  return (
    <article className="answer-card" data-testid="soren-margin-card">
      <div className="answer-head">
        <span className="answer-kicker">
          Margin · {data?.projectName || 'Roof Slope'} {data?.projectNumber || '#7005'}
        </span>
        <span className="answer-context">entered costs only</span>
      </div>

      <div className="margin-lines">
        <div className="margin-row">
          <span>Revised contract</span>
          <strong className="money">{revised}</strong>
        </div>
        <div className="margin-row">
          <span>Sub commitments + COs</span>
          <strong className="money negative">{commits}</strong>
        </div>
        <div className="margin-row">
          <span>Direct costs</span>
          <strong className="money negative">{directs}</strong>
        </div>
        <div className="margin-row margin-total">
          <span>Projected margin</span>
          <strong className="money">
            {projected} · {marginPct}
          </strong>
        </div>
      </div>

      <div className="completeness" data-testid="soren-margin-completeness">
        {completeness}
      </div>

      <div className="actions">
        {onAddCost && (
          <button className="btn primary" type="button" onClick={onAddCost}>
            Add a cost
          </button>
        )}
        {onOpenMargin && (
          <button className="btn" type="button" onClick={onOpenMargin}>
            Open Costs &amp; Margin
          </button>
        )}
      </div>
    </article>
  )
}
